import React, {useState} from "react";
import styled from "styled-components";
import {getTheme} from "../shared/themeUtils";
import Button from "../components/Button";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {request} from "../shared/httpInterceptor";
import {useCountryLang} from "../hooks/useCountryLang";
import {IProductOption} from "../interfaces/iProductOption";
import {ICartItem} from "../interfaces/ICartItem";
import Icon from "./Icon";
import {useClientContext} from "../hooks/useContext";
import {useUserLoggedIn} from "../hooks/useUserLoggedIn";
import {getLocalStorageItem, setLocalStorageItem} from "../shared/localStorageUtil";
import {toast} from "react-toastify";
import {useMessages} from "../hooks/useMessages";

const SetupOptionsContainer = styled.div`
    margin-top: ${getTheme("space.2")};
    border-top: 1px solid ${getTheme("colors.border")};
    padding-top: ${getTheme("space.2")};

    .option {
        font-size: ${getTheme("fontSizes.sm")};
        display: flex;
        flex-direction: column;

        .remark-input {
            padding: ${getTheme("space.1")};
            border-radius: ${getTheme("borderRadius")};
            border: 1px solid ${getTheme("colors.border")};
            margin-bottom: ${getTheme("space.2")};
            width: 100%;
            max-width: 300px;
        }

        .bottom {
            display: flex;
            align-items: flex-start;
        }

        button {
            padding: ${getTheme('space.1')};
            margin-top: ${getTheme("space.1")};
        }

        .option-image {
            width: 80px;
            height: auto;
            margin-right: ${getTheme("space.2")};

            img {
                max-width: 100%;
                border-radius: ${getTheme("radii.default")};
                border-radius: ${getTheme("borderRadius")}
            }
        }

        .option-details {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;

            .action {
                display: flex;
                align-items: center;

                button {
                    margin-left: ${getTheme("space.1")};
                    margin-top: 0;
                    position: relative;
                }
            }

            .add-option-button {
                padding: 0;
            }

            .option-label {
                color: ${getTheme("colors.primary")};
            }

            .option-price {
                color: ${getTheme("colors.primary")};
                font-weight: 500;
            }
        }

        @media (max-width: ${getTheme("breakpoints.0")}) {
            flex-direction: column;
            align-items: center;

            .option-details {
                margin-top: ${getTheme("space.1")};

                .action {
                    flex-direction: column;

                    button {
                        background: ${getTheme("colors.background")};
                        margin-top: ${getTheme("space.1")};
                    }
                }
            }

            button {
                width: 100%;
                display: flex;
                justify-content: center;
            }
        }
    }
`;

type ProductSetupOptionProps = {
  options: IProductOption[],
  productId: number;
  quantity: number;
  shoppingCart: ICartItem[];
  closeModal?: any // used to close suggestions modal on addToCart
};

export const removeLoggedOutSetupOption = (productId: number) => {
  const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions")
  if (loggedOutSetupOptions) {
    const setupEntries = [...JSON.parse(loggedOutSetupOptions)]
    const filtered = setupEntries.filter(item =>
      item.productId != productId
    )
    setLocalStorageItem("loggedOutSetupOptions", JSON.stringify(filtered))
  }
}

export function ProductSetupOption(props: ProductSetupOptionProps) {
  const {options, productId, quantity} = props;
  const queryClient = useQueryClient();
  const countryLang = useCountryLang();
  const clientCtx = useClientContext();
  const isUserLoggedIn = useUserLoggedIn()
  const [localStorageUpdated, setLocalStorageUpdated] = useState(false);
  const messagesQuery = useMessages()
  const [remark, setRemark] = useState("")

  const isOptionInCart = (option: IProductOption, productId: number) => {
    if (isUserLoggedIn) {
      return props.shoppingCart.some(entry =>
        entry.setupOptions?.some(setupOption =>
          setupOption.optionSetId === option.optionSetId &&
          setupOption.productId === props.productId
        )
      );
    } else {
      const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions")
      if (loggedOutSetupOptions != null) {
        const entries = [...JSON.parse(loggedOutSetupOptions)]
        return entries.some(entry =>
          entry.optionSetId == option.productOptions[0].optionSetId &&
          entry.productId == productId
        )
      }
      return false
    }
  };

  const addProductSetupOption = async (setupOptionData: any) => {
    const response = await request({
      url: `${countryLang}/addProductSetupOption`,
      method: 'POST',
      data: setupOptionData,
    });
    return response.data;
  };

  const mutation = useMutation(addProductSetupOption, {
    onSuccess: () => {
      setRemark("")
      queryClient.invalidateQueries();
      toast(messagesQuery.data.messages["product-added-to-cart"], {
        type: "success"
      })
    },
    onSettled: () => {
      props.closeModal(true)
    }
  });

  const handleAddOption = (option: IProductOption) => {
    const setupOptionData = {
      productId: productId,
      optionProductQuantity: quantity,
      optionSetId: option.optionSetId,
      optionId: option.productOptions[0].optionId,
      optionProductId: option.productOptions[0].setupProductId,
      optionPrice: option.productOptions[0].price,
      optionRemarks: remark
    };
    if (isUserLoggedIn) {
      mutation.mutate(setupOptionData);
    } else {
      // We check if we already have the entry in localStorage and push to it
      const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions")
      if (loggedOutSetupOptions) {
        const setupEntries = [...JSON.parse(loggedOutSetupOptions)]
        if (!setupEntries.some(item =>
          item.optionSetId == setupOptionData.optionSetId &&
          item.productId == setupOptionData.productId
        )) {
          setupEntries.push(setupOptionData)
          setLocalStorageItem("loggedOutSetupOptions", JSON.stringify(setupEntries))
          setLocalStorageUpdated(!localStorageUpdated)
        }
      }
      // Otherwise we create it with the first entry
      else {
        setLocalStorageItem("loggedOutSetupOptions", JSON.stringify([setupOptionData]))
        setLocalStorageUpdated(!localStorageUpdated)
      }

      // if it's used inside a modal, like addToCart suggestion modal
      props.closeModal && props.closeModal(true)
      toast(messagesQuery.data.messages["product-added-to-cart"], {
        type: "success"
      })

      queryClient.invalidateQueries(["loggedOutCartTotal"])
    }
  };

  const removeProductSetupOption = async (setupOptionData: any) => {
    const response = await request({
      url: `${countryLang}/deleteProductSetupOption`,
      method: 'POST',
      data: setupOptionData,
    });
    return response.data;
  };

  const removeMutation = useMutation(removeProductSetupOption, {
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const handleRemoveOption = (option: IProductOption) => {
    const setupOptionData = {
      productId: productId,
      optionProductQuantity: quantity,
      optionSetId: option.optionSetId,
      optionId: option.productOptions[0].optionId,
      optionProductId: option.productOptions[0].setupProductId,
      optionPrice: option.productOptions[0].price,
    };
    if (isUserLoggedIn) {
      removeMutation.mutate(setupOptionData);
    } else {
      removeLoggedOutSetupOption(setupOptionData.productId)
      setLocalStorageUpdated(!localStorageUpdated)
      queryClient.invalidateQueries(["loggedOutCartTotal"])
    }
  };

  return (
    <SetupOptionsContainer>
      {options.map((option) => (
        <div key={option.optionSetId} className="option">
          {messagesQuery.data &&
            (option.remarkMode == "O" || option.remarkMode == "M") &&
            <div className="top">
              <input value={remark}
                     className={"remark-input"}
                     placeholder={messagesQuery.data.messages["remark"]}
                     onChange={(e: any) => setRemark(e.target.value)}/>
            </div>
          }
          <div className="bottom">
            <div className="option-image">
              <img src={option.productOptions[0].filepath} alt={option.productOptions[0].name}/>
            </div>
            <div className="option-details">
                        <span className="option-label">
                            {option.productOptions[0].name}
                        </span>

              <div className={"action"}>
                          <span className="option-price">
                            {option.productOptions[0].price} {clientCtx && clientCtx.data?.currency}
                          </span>
                {!isOptionInCart(option, productId) ? (
                  <Button
                    variant="transparent"
                    className="action-button"
                    onClick={() => handleAddOption(option)}
                    isLoading={mutation.isLoading && mutation.variables?.optionSetId === option.optionSetId}
                    disabled={mutation.isLoading}
                  >
                    <Icon variant="small">shopping-cart-line</Icon>
                  </Button>
                ) : (
                  <Button
                    variant="transparent"
                    className="action-button"
                    onClick={() => handleRemoveOption(option)}
                    isLoading={removeMutation.isLoading && removeMutation.variables?.optionSetId === option.optionSetId}
                    disabled={removeMutation.isLoading}
                  >
                    <Icon variant={"small"}>delete-bin</Icon>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </SetupOptionsContainer>
  );
}
